/**
 * Copyright (c) 2021, Ouster, Inc.
 * All rights reserved.
 */
import React, { useEffect } from 'react';
import styled from 'styled-components/macro';

import { useStoreState, useStoreActions } from '../reducers';

// How long the toast will appear on screen
const ANIMATION_DURATION_S = 2.5;

const StyledToast = styled.div`
  /* Toast should always appear at the bottom of the screen */
  position: fixed;
  bottom: 64px;
  left: 50%;

  /* Toast should be at the foreground */
  z-index: var(--z-toast);

  /* Based on Figma */
  display: flex;
  padding: 16px;
  border-radius: 4px;
  background-color: var(--color-black-hole);
  color: var(--color-milky-way);
  border: 1px solid var(--color-galaxy-gray);

  /* Hidden */
  opacity: 0;
  transform: translateX(-50%) scale(0.4);

  /* Revealed */
  &.revealed {
    opacity: 1;
    transform: translateX(-50%) scale(1);
  }

  /* Make the transition between the two smooth */
  transition: opacity 240ms ease-in-out, transform 240ms ease-in-out;
`;

const Toast: React.FC = () => {
  const { toastMessage, toastShowing, latestToastLaunch } = useStoreState(
    (storeState) => ({
      ...storeState.toast,
    })
  );
  const { setToastShowing } = useStoreActions((actions) => actions.toast);

  useEffect(() => {
    let hideToastTimeout: NodeJS.Timeout;

    // Don't trigger anything on initialization
    if (latestToastLaunch) {
      hideToastTimeout = setTimeout(
        () => setToastShowing(false),
        ANIMATION_DURATION_S * 1000
      );
    }

    return () => {
      clearTimeout(hideToastTimeout);
    };
  }, [latestToastLaunch, setToastShowing]);

  return (
    <StyledToast className={toastShowing ? 'revealed' : ''}>
      {toastMessage}
    </StyledToast>
  );
};

export { Toast };
